import { useMutation, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { AppStrings } from "../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../utils/ErrorUtils";
import { bonusAdminEnvelope } from "./bonusAdminBody";
import { LIST_SERIES_QUERY_KEY } from "./useListSeries";

import type { SeriesIn, SeriesOut } from "../../components/Promotions/DynamicBonuses/types";

type StoredToken = { AccessToken?: string };

type CreateSeriesResult = { body: SeriesOut; message?: string };

async function postSeries(payload: SeriesIn): Promise<CreateSeriesResult> {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString ? JSON.parse(tokenString) : null;
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);
  const response = await instance.post(API_ROUTES.CREATE_DYNAMIC_BONUS_SERIES, payload);
  const envelope = bonusAdminEnvelope<SeriesOut>(response);
  if (!envelope.body) {
    throw new Error("Series creation returned no data");
  }
  // Unlike campaigns, a newly created series is config-only and must stay
  // `draft` until an explicit Activate on the detail page — do not auto-activate.
  return { body: envelope.body, message: envelope.message };
}

export const useCreateSeries = () => {
  const queryClient = useQueryClient();

  return useMutation<CreateSeriesResult, Error, SeriesIn>({
    mutationFn: postSeries,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: LIST_SERIES_QUERY_KEY });
      message.success(result.message || AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED);
    },
    onError: (error) => {
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    },
  });
};
